import { Build } from '@stencil/core';

import PubSub from './pub-sub';
import { getPlacementFromStore, setPlacementToStore } from './store';
import { AffiliatePossibleCharacteristics } from '../../../types/base/affiliate/characteristics';
import { AffiliatePlacement } from '../../../types/base/affiliate/placement';
import { AffiliateProperty } from '../../../types/base/affiliate/property';
import { AffiliateControllerResponse } from '../../../types/controllers/affiliate';
import { getQueryParamString } from '../../../util/string';
import { PlacementParams } from '../types';

import { getBaseUrl, getQueryParamFromUrl, isValidPaidMarketingParam, requiredParamsCheck, formatPromotions } from '.';

const pubSub = new PubSub();

export const fetchPlacement = (
    params: {
        placement_id: Extract<AffiliatePlacement['id'], number>;
        property_id?: Extract<AffiliateProperty['id'], number>;
    } & AffiliatePossibleCharacteristics,
    devMode: boolean
): Promise<AffiliateControllerResponse> => {
    const { placement_id: placementId, property_id: propertyId = 1, ...restParams } = params;
    const query = getQueryParamString(restParams);

    const baseUrl = getBaseUrl(devMode);
    const url = `${baseUrl}/v1/affiliate/properties/${propertyId}/placements/${placementId}/promotions${query}`;

    // eslint-disable-next-line no-console
    const fetchErr = (res: Response) => !Build.isTesting && console.error(res);
    return fetch(url)
        .then(res => {
            if (!res.ok) {
                return fetchErr(res);
            }

            return res.status !== 204 && res.json();
        })
        .catch(fetchErr);
};

export async function getPlacement(
    params: PlacementParams,
    cb?: (placement: AffiliateControllerResponse, location: string) => void,
    devMode = false,
    componentType?: string,
    ignorePubsub?: boolean
) {
    const hasMissingParams = requiredParamsCheck(
        {
            property_id: params.property_id,
            placement_id: params.placement_id,
        },
        componentType
    );

    if (hasMissingParams) {
        return;
    }

    const { location } = params;
    const paidMarketing = getQueryParamFromUrl('paidMarketing');

    if (paidMarketing && isValidPaidMarketingParam(paidMarketing)) {
        params.paid_marketing = paidMarketing;
    }

    const sortedParams = {};
    Object.keys(params)
        .sort()
        .forEach(key => (sortedParams[key] = params[key]));

    // if ignorePubsub, do the fetch and return the placement
    if (ignorePubsub === true) {
        const placementData = await fetchPlacement(params, devMode);
        const locationToUse = location || placementData.location;
        cb ? cb(placementData, locationToUse) : (this.placement = placementData);
    }

    const placementKey = getQueryParamString(sortedParams);
    let placement = getPlacementFromStore(placementKey);

    if (!placement) {
        // CASE -- placement does not exist in the store
        // set isLoading to true so other components don't fetch as well
        placement = { isLoading: true, data: null };
        setPlacementToStore(placementKey, placement);
        const placementData = await fetchPlacement(params, devMode);

        // If request failed, placement doesn't exist, or it is inactive then set isLoading to false and do nothing
        if (!placementData || !placementData.is_active) {
            setPlacementToStore(placementKey, { ...placement, isLoading: false });
            return;
        }

        // publish the fetched data to the pubSub
        pubSub.publish(placementKey, placementData);

        // update the placement data and set isLoading to false
        // then update the placement in the store
        placement = { isLoading: false, data: placementData };
        setPlacementToStore(placementKey, placement);

        const locationToUse = location || placement.data.location;
        cb ? cb(placement.data, locationToUse) : (this.placement = placement.data);
    } else {
        // CASE -- placement exists in the store
        if (!placement.isLoading) {
            // CASE -- the placement is not loading
            // if data doesn't exist or is inactive then early return
            if (!placement.data || !placement.data.is_active) {
                return;
            }
            const locationToUse = location || placement.data.location;
            cb ? cb(placement.data, locationToUse) : (this.placement = placement.data);
        } else {
            // CASE -- the placement is loading
            // subscribe to the pubSub with the placementKey
            pubSub.subscribe(placementKey, placementData => {
                // unsubscribe from the pubSub after data is published
                pubSub.unsubscribe(placementKey);

                const locationToUse = location || placementData.location;
                cb ? cb(placementData, locationToUse) : (this.placement = placementData);
            });
        }
    }
}

export async function fetchPromo(propertyId: number, promotionId: number, context: string, devMode: boolean) {
    try {
        const baseUrl = getBaseUrl(devMode);
        const url = `${baseUrl}/v1/affiliate/properties/${propertyId}/promotions/${promotionId}${
            context ? `?context=${context}` : ''
        }`;

        const res = await fetch(url);
        const response = await res.json();

        if (response.error) {
            // eslint-disable-next-line no-console
            console.error(`BAM Error fetching promotion: ${response.error}`);
            return;
        }

        const formattedPromo = formatPromotions([response.promotion], {
            allowAltLink: true,
            promotionListId: null,
            location: response.location ?? null,
            context: context,
            propertyId: Number(propertyId),
        })[0];

        return formattedPromo;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`BAM Error fetching promotion: ${error}`);
    }
}

export async function fetchFeaturedPromo(propertyId: number, affiliateId: number, context: string, devMode: boolean) {
    try {
        const baseUrl = getBaseUrl(devMode);
        const url = `${baseUrl}/v1/affiliate/properties/${propertyId}/affiliates/${affiliateId}/promotions/featured${
            context ? `?context=${context}` : ''
        }`;

        const res = await fetch(url);
        const response = await res.json();

        if (response.error) {
            // eslint-disable-next-line no-console
            console.error(`BAM Error fetching promotion: ${response.error}`);
            return;
        }

        const formattedPromo = formatPromotions([response.promotion], {
            allowAltLink: true,
            promotionListId: null,
            location: null,
            context: context,
            propertyId: Number(propertyId),
        })[0];

        return formattedPromo;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`BAM Error fetching promotion: ${error}`);
    }
}
